import React from 'react';
import { Button, Tooltip, theme } from 'antd';
import { motion } from 'framer-motion';
import { IconMicrophone, IconPlayerStopFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { CORAL } from '../theme/creamyTokens';

interface DictateButtonProps {
  listening: boolean;
  onStart: () => void;
  onStop: () => void;
  /** Hidden entirely when the browser has no recogniser -- see useSpeechDictation. */
  supported: boolean;
  /** Shown next to the microphone while it is off, when there is room for it. */
  label?: string;
  disabled?: boolean;
}

/**
 * The microphone. One of these, everywhere something can be dictated.
 *
 * While it is listening it stops being a microphone and becomes a stop button,
 * because that is the only thing anybody wants from it at that moment, and it
 * pulses so it is obvious from arm's length that the phone is still recording
 * -- a microphone somebody thinks they turned off is a small betrayal.
 */
const DictateButton: React.FC<DictateButtonProps> = ({ listening, onStart, onStop, supported, label, disabled }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  if (!supported) return null;

  const title = translate(t, listening ? TRANSLATIONS.DICTATE_STOP : TRANSLATIONS.DICTATE_START);

  return (
    <Tooltip title={title}>
      <motion.div
        animate={listening ? { scale: [1, 1.06, 1] } : { scale: 1 }}
        transition={listening ? { duration: 1.1, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
        style={{ display: 'inline-flex' }}
      >
        <Button
          type={listening ? 'primary' : 'default'}
          danger={listening}
          disabled={disabled}
          icon={listening ? <IconPlayerStopFilled size={15} /> : <IconMicrophone size={16} />}
          onClick={listening ? onStop : onStart}
          aria-label={title}
          style={{
            borderRadius: 999,
            borderColor: listening ? CORAL : token.colorBorder,
          }}
        >
          {!listening && label ? label : undefined}
        </Button>
      </motion.div>
    </Tooltip>
  );
};

export default DictateButton;
